import { defineStore } from "pinia"
import { ref, computed } from "vue"
import MemberService from "@/services/resources/MemberService"

const serviceMember = MemberService.build()

interface LayoutBreakpoint {
  breakpoint: number
  breakpointLock?: number
}

interface Sidebar {
  collapsible: boolean
  visible: boolean
  locked: boolean
}

interface PromotionPeriod {
  start: string
  end: string
  startDate?: string
  endDate?: string
}

interface PromotionApp {
  active: boolean
  mobileActive: boolean
  period: PromotionPeriod | null
  appStoreLink: string
  googlePlayLink: string
}

export const useLayoutStore = defineStore('layout', () => {
  // State
  const mobile = ref<number>(0)
  const layout = ref<LayoutBreakpoint>({
    breakpoint: 3,
    breakpointLock: undefined,
  })
  const sidebar = ref<Sidebar>({
    collapsible: false,
    visible: localStorage.getItem("sidebarVisible") !== "false",
    locked: false,
  })
  const promotionApp = ref<PromotionApp>({
    active: false,
    mobileActive: false,
    period: null,
    appStoreLink: '',
    googlePlayLink: '',
  })

  // Getters
  const getLayout = computed(() => layout.value)
  const getSidebar = computed(() => sidebar.value)
  const getIsMobile = computed(() => mobile.value < 768)
  const getIsMedium = computed(() => mobile.value >= 768 && mobile.value < 1024)
  const getPromotionData = computed(() => promotionApp.value)
  const getPromotionPeriod = computed(() => promotionApp.value.period)
  const getPromotionActive = computed(() => promotionApp.value.active)
  const getPromotionMobileActive = computed(() => promotionApp.value.mobileActive)
  const getPromotionStartDate = computed(() => promotionApp.value.period?.start)
  const getPromotionEndDate = computed(() => promotionApp.value.period?.end)
  const getAppStoreUrl = computed(() => promotionApp.value.appStoreLink)
  const getGooglePlayUrl = computed(() => promotionApp.value.googlePlayLink)

  // Actions
  function actionSetMobile(value: number) {
    mobile.value = value
  }

  function actionToggleSidebar() {
    const current = sidebar.value.visible
    const next = !current
    sidebar.value = { ...sidebar.value, visible: next }
    localStorage.setItem("sidebarVisible", String(next))
  }

  function actionHideSidebar() {
    sidebar.value = { ...sidebar.value, visible: false }
    localStorage.setItem("sidebarVisible", "false")
  }

  function actionShowSidebar() {
    sidebar.value = { ...sidebar.value, visible: true }
    localStorage.setItem("sidebarVisible", "true")
  }

  function actionSetSidebarLocked(value: boolean) {
    sidebar.value = { ...sidebar.value, locked: value }
  }

  function actionSetSidebarCollapsable(value: boolean) {
    if (sidebar.value.locked) {
      return
    }
    if (value) {
      // Going to mobile/tablet — hide sidebar but DON'T persist to localStorage
      sidebar.value = { ...sidebar.value, collapsible: true, visible: false }
    } else {
      // Going to desktop — always show sidebar
      sidebar.value = { ...sidebar.value, collapsible: false, visible: true }
      localStorage.setItem("sidebarVisible", "true")
    }
  }

  function actionRefreshLayout() {
    if (mobile.value > 1140) {
      layout.value = { ...layout.value, breakpoint: 3 }
      sidebar.value = { ...sidebar.value, locked: false }
      actionSetSidebarCollapsable(false)
      document.documentElement.setAttribute("data-device", "desktop")
    }
    if (mobile.value <= 1140) {
      layout.value = { ...layout.value, breakpoint: 2 }
      actionSetSidebarCollapsable(true)
      document.documentElement.setAttribute("data-device", "tablet")
    }
    if (mobile.value <= 600) {
      layout.value = { ...layout.value, breakpoint: 1 }
      actionSetSidebarCollapsable(true)
      document.documentElement.setAttribute("data-device", "smartphone")
    }
  }

  async function actionFetchPromotionStatus() {
    const response = await serviceMember.read(
      '/global-settings/promotion-app/status?key=IS_PROMOTING_ACTIVE'
    )

    promotionApp.value = {
      active: response.active,
      mobileActive: response.mobileActive,
      period: response.period,
      appStoreLink: response.appStoreLink,
      googlePlayLink: response.googlePlayLink,
    }
  }

  async function actionAssociate(memberIds: unknown) {
    const response = await serviceMember.post("/associate", memberIds)
    return response
  }

  async function actionDisassociate(memberIds: Record<string, unknown>) {
    const payload = {
      id: 'disassociate',
      ...memberIds,
    }
    const response = await serviceMember.update(payload)
    return response
  }

  async function actionGetFilteredMemberIds(params: string) {
    const response = await serviceMember.read(`/ids?${params}`)
    return response
  }

  return {
    // State
    mobile,
    layout,
    sidebar,
    promotionApp,

    // Getters
    getLayout,
    getSidebar,
    getIsMobile,
    getIsMedium,
    getPromotionData,
    getPromotionPeriod,
    getPromotionActive,
    getPromotionMobileActive,
    getPromotionStartDate,
    getPromotionEndDate,
    getAppStoreUrl,
    getGooglePlayUrl,

    // Actions
    actionSetMobile,
    actionToggleSidebar,
    actionHideSidebar,
    actionShowSidebar,
    actionSetSidebarLocked,
    actionSetSidebarCollapsable,
    actionRefreshLayout,
    actionFetchPromotionStatus,
    actionAssociate,
    actionDisassociate,
    actionGetFilteredMemberIds,
  }
})
